import React, { useState, useEffect } from 'react';
import api from '~/services/api';
import { MultiContaBancoProps, ContaProps } from './protocols';
import Tippy from '@tippyjs/react';
import { Form } from 'react-bootstrap';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Container, InputMultiContas } from './styles';
import { useQuery, useQueryClient } from 'react-query';

export const MultiContaBanco: React.FC<MultiContaBancoProps> = ({
  disabled,
  onChange,
  resetContas = false,
  lojas,
}) => {
  const queryClient = useQueryClient();
  const [all, setAll] = useState<boolean>(false);
  const [contas, setContas] = useState<ContaProps[]>([]);

  useEffect(() => {
    if (resetContas) {
      const newContas = contas.map((c: ContaProps) => ({
        ...c,
        flg_checked: false,
      }));
      setAll(false);
      setContas(newContas);
    }
  }, [resetContas]);

  const cod_tela_active = queryClient.getQueryData<{ cod_tela: number }>(
    'cod_tela_active',
  );

  const { isLoading } = useQuery({
    queryKey: ['get-multi-contas', lojas],
    queryFn: async () => {
      const { data: fields } = await api.get(`contas/lojas/${lojas}`);
      if (fields.success) {
        return setContas(fields.data);
      }
      return [];
    },
    enabled: !!lojas && cod_tela_active?.cod_tela === 315,
    retry: 2,
  });

  const getBancoIconName = (cod_banco: number) => {
    switch (cod_banco) {
      case 1:
        return 'banco-do-brasil';
      case 33:
        return 'santander';
      case 104:
        return 'caixa-economica-federal';
      case 237:
        return 'bradesco';
      case 341:
        return 'itau';
      case 399:
        return 'hsbc';
      case 756:
        return 'sicoob';
      case 422:
        return 'safra-bank';
      case 356:
        return 'banco-real';
      case 246:
        return 'abc-bank';
      case 260:
        return 'nubank';
      case 77:
        return 'inter';
      default:
        return 'cofre';
    }
  };

  const handleSetAllContas = () => {
    setAll(!all);
    const newContas = contas.map((c: ContaProps) => ({
      ...c,
      flg_checked: !all,
    }));
    setContas(newContas);
    const newValue = newContas.map((c: ContaProps) => c.cod_cc);
    return onChange(newValue);
  };

  const onChangeValue = (id: number) => {
    const newContas = contas.map((c: ContaProps) => {
      if (c.cod_cc === id) {
        return {
          ...c,
          flg_checked: !c.flg_checked,
        };
      }
      return c;
    });

    setContas(newContas);
    const todosMarcados = newContas.every((c: ContaProps) => c.flg_checked);
    setAll(todosMarcados);

    const filtredContas = newContas.filter((c: ContaProps) => c.flg_checked);
    const newValue = filtredContas.map((c: ContaProps) => c.cod_cc);

    onChange(newValue);

    onChange(newValue);
  };

  return (
    <Container>
      <label htmlFor="input-contas">Contas:</label>
      <InputMultiContas id="input-multi-contas">
        {isLoading && (
          <CircularProgress
            size={15}
            style={{ color: '#8850bf', margin: '10px 0px 0px 10px' }}
          />
        )}
        {!isLoading && (
          <Tippy
            placement="top"
            content="TODAS AS CONTAS"
            arrow
            animation="scale"
          >
            <div
              onClick={() => {
                if (!disabled) handleSetAllContas();
              }}
            >
              <Form.Check
                className="ButtonConta"
                type="checkbox"
                label="Todas"
                disabled={disabled}
                checked={all}
                onChange={() => null}
                style={{
                  backgroundColor: all ? '#57069e' : '#8850bf',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              />
            </div>
          </Tippy>
        )}
        {contas &&
          !isLoading &&
          contas.map((conta: ContaProps) => {
            const iconName = getBancoIconName(Number(conta.cod_banco));
            const iconPath = `/images/logoDeBancos/${iconName}.svg`;
            return (
              <Tippy
                placement="top"
                content={`Conta ${conta.des_cc} - ${conta.cod_banco}`}
                key={conta.cod_cc}
                arrow
                animation="scale"
              >
                <div
                  onClick={() => {
                    if (!disabled) onChangeValue(conta.cod_cc);
                  }}
                >
                  <Form.Check
                    className="ButtonConta"
                    type="checkbox"
                    disabled={disabled}
                    label={
                      <div
                        style={{
                          display: 'flex',
                          marginTop: '2px',
                          alignItems: 'center',
                          gap: '7px',
                        }}
                      >
                        <img
                          src={iconPath}
                          alt={iconName}
                          width={18}
                          height={18}
                        />
                        <span>{conta.des_cc}</span>
                      </div>
                    }
                    checked={conta.flg_checked}
                    onChange={() => null}
                    style={{
                      backgroundColor: conta.flg_checked
                        ? '#57069e'
                        : '#8850bf',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                  />
                </div>
              </Tippy>
            );
          })}
      </InputMultiContas>
    </Container>
  );
};
